import { NOTARY_KEYWORDS_TOP, NOTARY_KEYWORDS_TOP_TOTAL } from "@/content/notary-corpus/top.gen";
import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";

import { NOTARY_FAQ_CATEGORIES } from "@/content/notary-corpus/faq-defs";

export const Route = createFileRoute("/en/notary/faq/")({
  loader: async (): Promise<{ NOTARY_FAQ_EN: { q: string; a: string }[]; NOTARY_KEYWORDS: string[] }> => {
    const [m0, m1] = await Promise.all([
      import("@/content/notary-corpus/en"),
      import("@/content/notary-corpus/keywords"),
    ]);
    return { NOTARY_FAQ_EN: m0.NOTARY_FAQ_EN, NOTARY_KEYWORDS: m1.NOTARY_KEYWORDS };
  },
  component: EnNotaryFaqHub,
  head: ({ loaderData }) => {
    const { NOTARY_FAQ_EN = [], NOTARY_KEYWORDS = [] } = loaderData ?? {};

    const site = getActiveSiteUrl();
    const title = `Notary Public Thailand — 250+ FAQs for Expats (2026)`;
    const description = `Complete FAQ on Notarial Services in Thailand — 250+ questions on fees, procedures, Apostille legalization, Power of Attorney, document types, and cross-border recognition. Updated for 2026 by licensed Notarial Services Attorneys.`;
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: "/en/notary/faq",
        type: "article",
        locale: "en_US",
        alternates: [{ lang: "th", path: "/notary/faq" }],
        keywords: NOTARY_KEYWORDS_TOP.slice(0, 80),
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: site },
              { name: "Notary Public FAQ", url: `${site}/en/notary/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(NOTARY_FAQ_EN)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            speakable: {
              "@type": "SpeakableSpecification",
              cssSelector: ["h1", "h2", ".faq-answer"],
            },
            keywords: NOTARY_KEYWORDS_TOP.slice(0, 100).join(", "),
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/notary/faq")) },
      ],
    };
  },
});

function EnNotaryFaqHub() {
  const { NOTARY_FAQ_EN, NOTARY_KEYWORDS } = Route.useLoaderData() as { NOTARY_FAQ_EN: { q: string; a: string }[]; NOTARY_KEYWORDS: string[] };
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span>{" "}
          <span>Notary Public FAQ</span>
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold mb-3">
          Notary Public Thailand — {NOTARY_FAQ_EN.length}+ FAQs for Expats (2026)
        </h1>
        <p className="text-muted-foreground mb-8">
          Comprehensive Q&amp;A on Notarial Services in Thailand — fees, procedures, Apostille chain-legalization,
          Power of Attorney, affidavits, and cross-border document recognition. Answered by licensed Notarial Services
          Attorneys registered with the Lawyers Council of Thailand.
        </p>

        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>Thai version:</strong>{" "}
          <a href="/notary/faq" className="text-primary underline">
            ภาษาไทย — 240+ คำถามยอดฮิต →
          </a>
        </div>

        <section aria-label="Notary FAQ categories" className="mb-8">
          <h2 className="text-lg font-semibold mb-3">Browse by category</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {NOTARY_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/en/notary/faq/${c.slug}`} className="rounded-lg border bg-card p-3 text-sm hover:border-primary hover:shadow-sm transition">
                <div className="font-semibold mb-1">{c.titleEn}</div>
                <div className="text-xs text-muted-foreground line-clamp-2">{c.descEn}</div>
              </a>
            ))}
          </div>
        </section>

        <div className="space-y-4">
          {NOTARY_FAQ_EN.map((item, i) => (
            <details
              key={i}
              className="rounded-lg border bg-card p-4 open:shadow-sm"
              {...(i < 5 ? { open: true } : {})}
            >
              <summary className="cursor-pointer font-semibold">
                <span className="text-muted-foreground mr-2">Q{i + 1}.</span>
                {item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <section className="mt-12 rounded-lg border p-6 bg-primary/5">
          <h2 className="text-xl font-bold mb-2">Still have questions?</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Free consultation with a licensed Notarial Services Attorney — reply within 15 minutes (Mon-Sat, 9:00-18:00 ICT).
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 text-primary-foreground font-medium">
            Contact us →
          </a>
        </section>

        <section className="mt-12">
          <h2 className="text-xl font-bold mb-3">Popular Notary Public Topics ({NOTARY_KEYWORDS_TOP_TOTAL} keywords)</h2>
          <p className="text-sm text-muted-foreground mb-4">
            The complete keyword universe our Notarial Services Attorneys cover — nationwide across Bangkok,
            Phuket, Chiang Mai, Pattaya, and every major province.
          </p>
          <div className="flex flex-wrap gap-2 text-xs">
            {NOTARY_KEYWORDS.map((kw, i) => (
              <span key={i} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground">
                {kw}
              </span>
            ))}
          </div>
        </section>
      
        <FaqSourceNote topic="notary" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}